import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Card, Col, Row, Space } from 'antd';
import isObject from 'lodash/isObject';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useIntl } from 'react-intl';
import useNavigationSource from '../../../../../app/hooks/useNavigationSource';
import appRoutes from '@/configs/routes';
import { ActionButton, BreadCrumbs, IntlMessages } from '@/components';
import FormEditDictionary from '../../../forms/FormEditDictionary';
import DictionariesProvider from '../../../providers/DictionariesProvider';
import getFeatureSourceTranslation from '../../../../Featured/utils/getFeatureSourceTranslation';
import { useGetDictionaryQuery, useUpdateDictionaryMutation } from '../../../api/dictionariesApiSlice';

const PageDictionaryEdit = () => {
  const { id } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchQueryParams] = useSearchParams();
  const [dictionaryErrors, setEditDictionaryErrors] = useState(null);
  const { urlPrefix, navigationSource } = useNavigationSource();

  const { data, isLoading } = useGetDictionaryQuery({ id, include: 'structure' });
  const [updateDictionary, { isLoading: isDictionaryUpdating }] = useUpdateDictionaryMutation();

  const multipleSources = searchQueryParams.get('multipleSources') ?? null;

  const handleUpdateDictionary = async (values) => {
    updateDictionary({ dictionaryId: id, values })
      .unwrap()
      .then(({ data: dictionary }) => {
        navigate(
          appRoutes.urlPageTranslationDetails({
            urlPrefix,
            id: dictionary.id,
            source: navigationSource,
            multipleSources,
          }),
        );
      })
      .catch((error) => {
        if (isObject(error?.data)) {
          setEditDictionaryErrors(error.data.errors);
        }
      });
  };

  return (
    <>
      <Row gutter={[32, 32]} justify="space-between" className="hp-mb-12" align="middle">
        <BreadCrumbs
          breadCrumbParent={
            urlPrefix ? (
              <Link to={appRoutes.urlPageFeatures({ featureSource: urlPrefix })}>
                <IntlMessages id={getFeatureSourceTranslation(urlPrefix)} />
              </Link>
            ) : null
          }
          breadCrumbParent2={
            <Link
              to={appRoutes.urlPageDictionaries({ urlPrefix, source: navigationSource, multipleSources })}
            >
              <IntlMessages id="dictionaries-breadcrumbs" />
            </Link>
          }
          breadCrumbActive={<IntlMessages id="ui-general-edit" />}
        />

        <Col md={10} span={24} className="text-end">
          <Space size="middle">
            <ActionButton
              title={intl.formatMessage({ id: 'ui-general-back-to-list' })}
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() =>
                navigate(appRoutes.urlPageTranslationDetails({ urlPrefix, id }), {
                  state: { key: 'prev_dictionary_edit' },
                  replace: true,
                })
              }
            />
          </Space>
        </Col>
      </Row>

      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Card
            title={<IntlMessages id="dictionaries-edit-title" />}
            className="hp-border-color-black-40 hp-card-6"
          >
            <DictionariesProvider edit>
              <FormEditDictionary
                initialValues={data || {}}
                isSubmitting={isDictionaryUpdating || isLoading}
                formErrors={dictionaryErrors}
                onSubmit={handleUpdateDictionary}
              />
            </DictionariesProvider>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageDictionaryEdit;
